import React, { useState, useEffect } from "react";
import { base44 } from "@voult/api/base44Client";
import { Store, Plus, Loader2, Trash2, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const STATUS_STYLES = {
  active: { cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
  suspended: { cls: "bg-yellow-100 text-yellow-700", icon: AlertCircle },
  revoked: { cls: "bg-rose-100 text-rose-700", icon: XCircle },
};

export default function MerchantAccess() {
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ merchant_name: "", merchant_id: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    base44.entities.MerchantAccess.list("-created_date", 100).then(setMerchants).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.merchant_name.trim()) return;
    setSaving(true);
    await base44.entities.MerchantAccess.create({ ...form, status: "active", permissions: ["tokenize", "charge"] });
    setForm({ merchant_name: "", merchant_id: "", notes: "" });
    setShowForm(false);
    setSaving(false);
    load();
  };

  const updateStatus = async (merchant, status) => {
    await base44.entities.MerchantAccess.update(merchant.id, { status });
    load();
  };

  const remove = async (merchant) => {
    if (!confirm(`Remove "${merchant.merchant_name}"?`)) return;
    await base44.entities.MerchantAccess.delete(merchant.id);
    load();
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Merchant Access</h1>
          <p className="text-sm text-slate-500 mt-0.5">Control which merchants can use your payment tokens.</p>
        </div>
        <button onClick={() => setShowForm((s) => !s)}
          className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800 transition">
          <Plus className="h-4 w-4" /> Add merchant
        </button>
      </div>

      {showForm && (
        <div className="mb-5 bg-white rounded-2xl border border-slate-200 p-5">
          <h2 className="text-sm font-semibold text-slate-900 mb-4">New Merchant</h2>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Merchant Name *</label>
                <input value={form.merchant_name} onChange={(e) => setForm((f) => ({ ...f, merchant_name: e.target.value }))}
                  placeholder="Acme Corp" className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-slate-900" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Merchant ID</label>
                <input value={form.merchant_id} onChange={(e) => setForm((f) => ({ ...f, merchant_id: e.target.value }))}
                  placeholder="merchant_abc123" className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-slate-900" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Notes</label>
              <input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Internal notes…" className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-slate-900" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowForm(false)} className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
              <button onClick={create} disabled={saving || !form.merchant_name.trim()}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white disabled:opacity-50">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add merchant"}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : merchants.length === 0 ? (
        <div className="text-center py-20 text-slate-500">
          <Store className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          <p>No merchants configured.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
          {merchants.map((m) => {
            const s = STATUS_STYLES[m.status] || STATUS_STYLES.active;
            const StatusIcon = s.icon;
            return (
              <div key={m.id} className="flex items-center gap-4 px-5 py-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 flex-shrink-0">
                  <Store className="h-5 w-5 text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-sm font-medium text-slate-900">{m.merchant_name}</p>
                    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase rounded-full px-2 py-0.5 ${s.cls}`}>
                      <StatusIcon className="h-3 w-3" /> {m.status}
                    </span>
                  </div>
                  {m.merchant_id && <p className="text-xs font-mono text-slate-500">{m.merchant_id}</p>}
                  {m.notes && <p className="text-xs text-slate-400 mt-0.5">{m.notes}</p>}
                  {m.last_access && <p className="text-xs text-slate-400">Last access {formatDistanceToNow(new Date(m.last_access), { addSuffix: true })}</p>}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {m.status === "active" && (
                    <button onClick={() => updateStatus(m, "suspended")}
                      className="text-xs rounded-lg border border-yellow-200 px-2.5 py-1.5 text-yellow-700 hover:bg-yellow-50 transition">Suspend</button>
                  )}
                  {m.status === "suspended" && (
                    <button onClick={() => updateStatus(m, "active")}
                      className="text-xs rounded-lg border border-emerald-200 px-2.5 py-1.5 text-emerald-700 hover:bg-emerald-50 transition">Restore</button>
                  )}
                  <button onClick={() => remove(m)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}